import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, Phone, MapPin, Navigation, Stethoscope, ClipboardList } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FindClinic() {
  const [query, setQuery] = useState("");
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/clinics`, { params: query ? { q: query } : {} });
        setClinics(data);
      } catch {
        setClinics([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <main className="pt-[68px] min-h-screen" data-testid="find-clinic-page">
      <section className="relative z-10 bg-[color:var(--relumia-ink)] text-[color:var(--relumia-base)] py-16 md:py-20">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <div className="flex items-center gap-3 mb-5">
            <Stethoscope size={18} className="text-gold" strokeWidth={1.6} />
            <span className="overline !text-gold">Professional Network</span>
          </div>
          <h1 className="font-serif font-light tracking-tight leading-[1.02] text-4xl md:text-6xl">
            Find a <span className="italic text-gold">Dermatologist</span>
          </h1>
          <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-white/70">
            Connect with a skin specialist to review your protocol. Search by pincode, city, clinic or doctor.
          </p>

          <div className="mt-8 max-w-xl relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--relumia-muted)]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by city, pincode, clinic or doctor…"
              data-testid="clinic-search"
              className="w-full rounded-full bg-white pl-12 pr-5 py-3.5 text-[14px] text-[color:var(--relumia-ink)] placeholder:text-[color:var(--relumia-muted)]/70 focus:outline-none"
            />
          </div>
        </div>
      </section>

      <section className="relative z-10 py-14 md:py-20">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <div className="flex items-center justify-between mb-8">
            <span className="overline">{loading ? "Searching…" : `${clinics.length} clinic${clinics.length === 1 ? "" : "s"} found`}</span>
            <Link to="/skin-analysis" className="text-[13px] text-gold hover:text-[color:var(--relumia-gold-deep)] inline-flex items-center gap-2">
              <ClipboardList size={15} /> Build my protocol first
            </Link>
          </div>

          {!loading && clinics.length === 0 && (
            <div className="text-center py-16 text-[color:var(--relumia-muted)]" data-testid="clinic-empty">
              <p className="font-serif text-2xl text-[color:var(--relumia-ink)] mb-2">No clinics match that search.</p>
              <p className="text-[14px]">Try a different city, or clear the search to see all clinics.</p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            {clinics.map((c) => (
              <div key={c.id} data-testid={`clinic-card-${c.id}`} className="bg-base border border-champagne rounded-sm p-6 flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-serif text-2xl text-[color:var(--relumia-ink)] leading-tight">{c.name}</div>
                    <div className="text-[13px] text-gold mt-1">{c.doctor}</div>
                  </div>
                  <span className="text-[11px] rounded-full bg-surface border border-champagne px-3 py-1 text-[color:var(--relumia-muted)] whitespace-nowrap">{c.city}</span>
                </div>
                <div className="text-[12px] tracking-wide text-[color:var(--relumia-muted)] mt-2">{c.specialty}</div>
                <div className="flex items-start gap-2 mt-4 text-[13px] leading-relaxed text-[color:var(--relumia-muted)]">
                  <MapPin size={16} className="text-gold shrink-0 mt-0.5" strokeWidth={1.6} />
                  {c.address}
                </div>
                <div className="mt-6 flex gap-3 items-center">
                  {c.phone ? (
                    <a
                      href={`tel:${c.phone}`}
                      data-testid={`clinic-call-${c.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--relumia-ink)] px-5 py-3 text-[13px] tracking-wide text-white hover:bg-[color:var(--relumia-gold-deep)] transition-colors"
                    >
                      <Phone size={15} strokeWidth={2} /> Call Clinic
                    </a>
                  ) : (
                    <span
                      data-testid={`clinic-no-phone-${c.id}`}
                      className="flex-1 text-[11.5px] tracking-wide text-[color:var(--relumia-muted)] italic"
                    >
                      Phone not listed — please walk in or ask reception.
                    </span>
                  )}
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.name + " " + c.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[color:var(--relumia-ink)]/20 px-5 py-3 text-[13px] tracking-wide text-[color:var(--relumia-ink)] hover:border-[color:var(--relumia-gold)] transition-colors"
                  >
                    <Navigation size={15} /> Directions
                  </a>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-12 text-[11px] text-[color:var(--relumia-muted)] leading-relaxed max-w-2xl">
            Clinic listings are provided for convenience. JAKS Pharma does not endorse or guarantee any
            practitioner; please verify credentials independently. Always consult a qualified healthcare
            provider before starting any treatment.
          </p>
        </div>
      </section>
    </main>
  );
}
